import { Tabs, Card, Image, Button, Progress, Spin } from "antd";
import { TagSelector, SubjectSelector, LocationSelector, GallerySelector } from "./Selectors";

function GlobalMetadataForm({ value, onChange }) {
  return (
    <Card 
      title="Applied to all photos"
      style={{
        background: '#20202030', 
        border: '1px solid #404040', 
        color: '#eee',
        height: '100%'
      }}
      styles={{ 
        title: { color: '#eee' }, 
        body: { height: '100%', overflow: 'auto'}
      }}
    >
      <TagSelector
        value={value.tags}
        onChange={(tags) => onChange({ ...value, tags })}
      />

      <SubjectSelector
        value={value.subject}
        onChange={(subject) => onChange({ ...value, subject })}
      />

      <LocationSelector
        value={value.location}
        onChange={(location) => onChange({ ...value, location })}
      />

      <GallerySelector
        value={value.galleries}
        onChange={(galleries) => onChange({ ...value, galleries })}
      />
    </Card>
  );
}

function PhotoMetadataForm({ item, onChange, globalMetadata }) {

  const effective = {
    tags: item.metadata.tags ?? globalMetadata.tags,
    subject: item.metadata.subject ?? globalMetadata.subject,
    location: item.metadata.location ?? globalMetadata.location,
    galleries: item.metadata.galleries ?? globalMetadata.galleries,
  }

  const update = (key, value) => {
    const next = { ...item.metadata };

    // If value matches global, remove override
    if (
      value === globalMetadata[key] ||
      JSON.stringify(value) === JSON.stringify(globalMetadata[key])
    ) {
      delete next[key];
    } else {
      next[key] = value;
    }

    onChange(next);
  };

  return (
    <Card
      title="Overrides for this photo"
      extra={
        <Button
          danger
          size="small"
          onClick={() => onChange({})}
        >
          Clear overrides
        </Button>
      }
      style={{background: '#20202030', border: '1px solid #404040', color: '#eee'}}
      styles={{ title: { color: '#eee' } }}
    >
      <TagSelector
        value={effective.tags}
        onChange={(v) => update("tags", v)}
        allowClear
        isOverride={ effective.tags !== globalMetadata.tags }
      />

      <SubjectSelector
        value={effective.subject}
        onChange={(v) => update("subject", v)}
        allowClear
        isOverride={ effective.subject !== globalMetadata.subject }
      />

      <LocationSelector
        value={effective.location}
        onChange={(v) => update("location", v)}
        allowClear
        isOverride={ effective.location !== globalMetadata.location }
      />

      <GallerySelector
        value={effective.galleries}
        onChange={(v) => update("galleries", v)}
        allowClear
        isOverride={ JSON.stringify(effective.galleries) !== JSON.stringify(globalMetadata.galleries) }
      />
    </Card>
  );
}

function MetadataTabs({ items, setItems, globalMetadata, setGlobalMetadata }) {
  const tabItems = [
    {
      key: "global",
      label: "Global Defaults",
      children: (
        <div style={{ height: "100%", overflowY: "auto"}}>
        <GlobalMetadataForm
          value={globalMetadata}
          onChange={setGlobalMetadata}
        />
        </div>
      ),
    },
    ...items.map((item) => ({
      key: item.id,
      label: (
        <div style={{ width: 220 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Image
              src={item.previewUrl}
              width={32}
              height={32}
              style={{ objectFit: "cover", borderRadius: 4 }}
              preview={false}
            />

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  color: item.hasOverrides ? "#fa0" : "#fff",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: 13,
                }}
              >
                {item.file.name}
              </div>
              {item.status === "compressing" && <Spin size="small" />}
              {item.status === "pending" && (<div style={{ fontSize: 11, color: "#888" }}>
                {(item.compressedSize / 1024 / 1024).toFixed(1)} MB (was: {(item.originalSize / 1024 / 1024).toFixed(1)} MB)
              </div>)}
              
              {item.status !== "pending" && (
                <Progress
                  percent={item.progress}
                  size="small"
                  showInfo={false}
                  status={
                    item.status === "error"
                      ? "exception"
                      : item.status === "done"
                      ? "success"
                      : "active"
                  }
                  style={{ marginTop: 4 }}
                />
              )}
            </div>
          </div>
          {item.status === "error" && (
            <div style={{ fontSize: 11, color: "#ff4d4f" }}>
              Failed
            </div>
          )}
        </div>
      ),
      children: (
        <PhotoMetadataForm
          item={item}
          globalMetadata={globalMetadata}
          onChange={(metadata) => {
            setItems((prev) =>
              prev.map((p) =>
                p.id === item.id
                  ? {
                      ...p,
                      metadata,
                      hasOverrides: true,
                    }
                  : p
              )
            );
          }}
        />
      ),
    })),
  ];

  return <Tabs 
    items={tabItems} 
    type="line" 
    tabPosition="left" 
    tabBarStyle={{ maxHeight: '50vh', background: '#20202030', borderRadius: '8px' }} 
    style={{ maxHeight: '100%', display: 'flex' }}
  />;
}

export default MetadataTabs;